import moment from "moment";
import { GroupBy } from "../../OrderFlow/utils/orderFlowUtils";
// Check if two recipes have the same selected ingredient options (a duplicate customized recipe).
export const checkHasSameSelectedIngredients = (a, b) => {
    // If the arrays have different lengths, they can't be the same.
    if (a.length !== b.length)
        return false;
    // Iterate over array 'a' and check for a matching option in array 'b'.
    for (const option of a) {
        // Find a matching option in 'b' with the same originalIngredientId.
        const matchingOption = b.find((o) => o.originalIngredientId === option.originalIngredientId);
        // If no matching option is found, the arrays are different.
        if (!matchingOption)
            return false;
        // If the selected options have different lengths, they cannot be the same
        if (option.selectedOptions.length !== matchingOption.selectedOptions.length)
            return false;
        // Check if all selected options in the current option are present in the matching option.
        for (const selectedOption of option.selectedOptions) {
            const matchingSelectedOption = matchingOption.selectedOptions.find((so2) => so2.replacementIngredientOptionId === selectedOption.replacementIngredientOptionId &&
                so2.ReplacementQty === selectedOption.ReplacementQty);
            // If any selected option is missing, return false.
            if (!matchingSelectedOption)
                return false;
        }
    }
    // If we make it through all options without returning false, the arrays are the same.
    return true;
};
/**
 * Processes the meal list for a given meal date, summarizing meal details and recipes.
 *
 * @param mealList - The list of meals for a specific meal date.
 * @returns A summarized array of MealSummary objects.
 */
const getMealsList = (mealList) => {
    return mealList.reduce((acc, ml) => {
        var _a;
        // Find an existing meal summary for the current meal type.
        let targetMeal = acc.find((tm) => tm.name === ml.meal_type);
        // If no existing meal is found, create a new meal summary entry.
        if (!targetMeal) {
            targetMeal = {
                name: (_a = ml.meal_type) !== null && _a !== void 0 ? _a : ml.meal,
                programMealId: ml.ProgramMealId,
                quantity: 0,
                price: ml.meal_cost,
                recipes: [],
            };
            // Add the new meal to the accumulator.
            acc.push(targetMeal);
        }
        let mealWithoutRecipe = mealList.find(m => m.ProgramMealId === (targetMeal === null || targetMeal === void 0 ? void 0 : targetMeal.programMealId) && m.recipeName === null);
        if (mealWithoutRecipe) {
            targetMeal.price = mealWithoutRecipe.meal_cost;
        }
        targetMeal.quantity += ml.quantity; // Increment the meal quantity.
        if (!ml.recipeId)
            return acc; // If no recipe ID is provided, return the accumulator.
        // Initialize selected ingredient options.
        const selectedIngredientOptions = [];
        // Group recipe changes by the original ingredient ID.
        const groupedRecipeChanges = GroupBy("ingredient_original_id", ml.recipeChanges);
        // Process each group of recipe changes.
        Object.entries(groupedRecipeChanges).forEach(([key, value]) => {
            value.forEach((ingredient) => {
                var _a, _b, _c;
                // For each ingredient in the group, check if an option for the existing ingredient already exists.
                const existingOption = selectedIngredientOptions === null || selectedIngredientOptions === void 0 ? void 0 : selectedIngredientOptions.find((i) => i.originalIngredientId === Number(key));
                // Create a new option object for the ingredient.
                const newOption = {
                    replacementIngredientId: (_a = ingredient.ingredient_replacement_id) !== null && _a !== void 0 ? _a : undefined,
                    // We do not have replacementIngredientOptionId here and it's only used for the customize form
                    // so we can set it to ingredient_replacement_id
                    replacementIngredientOptionId: (_b = ingredient.ingredient_replacement_id) !== null && _b !== void 0 ? _b : undefined,
                    replacementIngredientName: ingredient.ingredient_replacement_name,
                    replacementPrice: ingredient.replacement_price,
                    Unit: undefined,
                    ReplacementQty: ingredient.replacement_quantity,
                };
                // If the option exists, add the new option to its selected options.
                if (existingOption) {
                    existingOption.selectedOptions.push(newOption);
                }
                else {
                    // If it doesn't exist, create a new entry in selectedIngredientOptions.
                    selectedIngredientOptions === null || selectedIngredientOptions === void 0 ? void 0 : selectedIngredientOptions.push({
                        mustBeReplaced: (_c = ingredient.must_be_replaced) !== null && _c !== void 0 ? _c : false,
                        originalIngredientId: ingredient.ingredient_original_id,
                        recipeIngredientId: ingredient.recipe_ingredient_id,
                        selectedOptions: [newOption],
                    });
                }
            });
        });
        // Check if any selected ingredient options must be replaced.
        const hasMustReplaceIngredients = selectedIngredientOptions.some((option) => option.mustBeReplaced);
        // Check if there are existing recipes with the same ID.
        const hasExistingRecipes = targetMeal.recipes.some((recipe) => recipe.recipeId === ml.recipeId);
        if (hasExistingRecipes) {
            // If there are matching recipes, filter them by ID.
            const existingRecipes = targetMeal.recipes.filter((recipe) => recipe.recipeId === ml.recipeId);
            // Check if any of the existing recipes have the same selected ingredients.
            const matchingRecipe = existingRecipes.find((recipe) => { var _a; return checkHasSameSelectedIngredients(selectedIngredientOptions, (_a = recipe === null || recipe === void 0 ? void 0 : recipe.selectedIngredientOptions) !== null && _a !== void 0 ? _a : []); });
            if (matchingRecipe) {
                // Increment the quantity if the recipe exists and has the same selected ingredients.
                matchingRecipe.quantity += ml.quantity;
                return acc;
            }
        }
        // Push the summarized recipe details into the target meal's recipes array.
        targetMeal.recipes.push({
            name: ml.recipeName,
            shortName: ml.recipeShortName,
            price: ml.meal_cost,
            quantity: ml.quantity,
            recipeId: ml.recipeId,
            replacementPrice: ml.recipeChanges.reduce((sum, ingredient) => sum + ingredient.replacement_price, 0),
            selectedIngredientOptions: selectedIngredientOptions,
            hasMustReplaceIngredients,
        });
        return acc;
    }, []);
};
/**
 * Maps a product item from OrderInfo to a ProductSummaryType object.
 * This is used to format product data for display in the order summary.
 *
 * @param p - The product item from OrderInfo.
 * @returns A formatted ProductSummaryType object.
 */
const mapProductToSummary = (p) => {
    var _a;
    return ({
        name: p.name,
        quantity: p.quantity,
        price: p.cost,
        id: p.productId,
        sale: (_a = p.SalePrice) !== null && _a !== void 0 ? _a : undefined,
        // Note: getUpcomingOrders query currently does not return DayOfWeek.
        dayOfWeek: moment(p.deliveryDate).isValid() ? moment(p.deliveryDate).day() : -1,
    });
};
/**
 * This function processes an order information object to summarize meal dates and details.
 * It extracts meal data for each date, grouping and organizing it for further use.
 *
 * @param orderInfo - An object containing information about the order, including meal dates and meal lists.
 * @returns An array of objects, each containing a date and the associated meals for that date.
 */
export const getOrderSummaryDates = (orderInfo) => {
    // Flatten the nested meal_dates array from each day.
    const allMealDates = orderInfo.days.flatMap((d) => d.meal_dates);
    const orderHasProducts = orderInfo.product_items && orderInfo.product_items.length > 0;
    // PRODUCT ONLY ORDER
    if (allMealDates.length === 0 && orderHasProducts) {
        const productsByDate = GroupBy("deliveryDate", orderInfo.product_items);
        return Object.entries(productsByDate).map(([date, products]) => ({
            date,
            meals: [],
            products: products.map(mapProductToSummary),
            isDayPastCutoff: false,
        }));
    }
    return allMealDates.map((md) => {
        var _a, _b, _c;
        // Map each meal_date object to a new structure.
        const mealDate = {
            date: moment(md.meal_date).format("YYYY-MM-DD"),
            // Reduce meal_list into a summarized format.
            meals: getMealsList(md.meal_list),
            products: (_c = (_b = (_a = orderInfo.product_items) === null || _a === void 0 ? void 0 : _a.filter((p) => moment(md.meal_date).isSame(p.deliveryDate, "day"))) === null || _b === void 0 ? void 0 : _b.map(mapProductToSummary)) !== null && _c !== void 0 ? _c : [],
            isDayPastCutoff: false,
        };
        return mealDate;
    });
};
